import { useState, useEffect, useCallback } from "react";
import { Briefcase, Loader2, Plus, Trash2, FileText, Mail, Phone, Power } from "lucide-react";
import { apiFetch, openPdf } from "@/portal/api";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";

const STATUS = [
  { key: "new", label: "Nová", cls: "bg-orange-500/15 text-orange-300" },
  { key: "reviewed", label: "Prohlédnuto", cls: "bg-blue-500/15 text-blue-300" },
  { key: "interview", label: "Pohovor", cls: "bg-violet-500/15 text-violet-300" },
  { key: "hired", label: "Přijat/a", cls: "bg-emerald-500/15 text-emerald-300" },
  { key: "rejected", label: "Zamítnuto", cls: "bg-zinc-600/30 text-zinc-400" },
];
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("cs-CZ", { day: "numeric", month: "short", year: "numeric" }) : "");

const SUGGESTIONS = [
  { title: "Obchodní zástupce", employment_type: "HPP", description: "Péče o klienty, poptávky a uzavírání smluv na dlouhodobý pronájem vozů." },
  { title: "Operátor zákaznické podpory", employment_type: "HPP", description: "Komunikace s klienty po telefonu i e-mailem, správa rezervací a dotazů." },
  { title: "Koordinátor vozového parku", employment_type: "HPP", description: "Správa flotily, přistavování a předávání vozů, hlídání servisů a STK." },
  { title: "Příprava a mytí vozů", employment_type: "DPP / brigáda", description: "Čištění, detailing a příprava vozů před předáním klientovi." },
  { title: "Řidič – přistavování vozů", employment_type: "DPP", description: "Rozvoz a přistavování vozů klientům po Praze a okolí." },
  { title: "Účetní / fakturant(ka)", employment_type: "HPP / ŽL", description: "Zpracování faktur, nákladů a podkladů pro účetnictví." },
  { title: "Marketingový specialista", employment_type: "HPP / ŽL", description: "Správa kampaní, sociálních sítí a inzerce vozů." },
];

const CareersTab = ({ token }) => {
  const [positions, setPositions] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", location: "", employment_type: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        apiFetch("/admin/careers/positions", { token }),
        apiFetch("/admin/careers/applications", { token }),
      ]);
      setPositions(p); setApps(a);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const addPosition = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Zadejte název pozice."); return; }
    setSaving(true);
    try {
      await apiFetch("/admin/careers/positions", { method: "POST", token, body: { ...form, active: true } });
      toast.success("Pozice přidána."); setForm({ title: "", location: "", employment_type: "", description: "" }); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  const togglePosition = async (p) => {
    try { await apiFetch(`/admin/careers/positions/${p.id}`, { method: "PATCH", token, body: { ...p, active: !p.active } }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const delPosition = async (p) => {
    if (!(await confirmDialog({ title: `Smazat pozici ${p.title}?`, confirmText: "Smazat", danger: true }))) return;
    try { await apiFetch(`/admin/careers/positions/${p.id}`, { method: "DELETE", token }); load(); } catch (e) { toast.error(e.message); }
  };
  const setStatus = async (a, status) => {
    try { await apiFetch(`/admin/careers/applications/${a.id}`, { method: "PATCH", token, body: { status } }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const delApp = async (a) => {
    if (!(await confirmDialog({ title: `Smazat přihlášku ${a.name}?`, confirmText: "Smazat", danger: true }))) return;
    try { await apiFetch(`/admin/careers/applications/${a.id}`, { method: "DELETE", token }); load(); } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="careers-tab">
      <div className="lg:col-span-1 space-y-4">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-4 inline-flex items-center gap-2"><Plus className="w-5 h-5 text-orange-400" /> Nová pozice</h3>
          <form onSubmit={addPosition} className="space-y-3" data-testid="position-form">
            <div data-testid="position-suggestions">
              <p className="text-[11px] text-zinc-500 mb-1.5">Rychlý výběr (pak uprav podle sebe):</p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button key={s.title} type="button" onClick={() => setForm({ title: s.title, location: form.location || "Praha", employment_type: s.employment_type, description: s.description })} className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-zinc-300 hover:border-orange-400/50 hover:text-orange-300 transition-colors" data-testid={`suggest-${s.title}`}>{s.title}</button>
                ))}
              </div>
            </div>
            <input className="input-dark" placeholder="Název pozice *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="position-title" />
            <div className="grid grid-cols-2 gap-2">
              <input className="input-dark" placeholder="Lokalita" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="position-location" />
              <input className="input-dark" placeholder="Úvazek (HPP…)" value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} data-testid="position-type" />
            </div>
            <textarea className="input-dark min-h-[80px]" placeholder="Popis pozice" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="position-description" />
            <button type="submit" disabled={saving} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="position-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat pozici</button>
          </form>
        </div>
        <div className="glass rounded-2xl p-5">
          <h4 className="text-sm font-semibold text-zinc-300 mb-3">Pozice ({positions.length})</h4>
          {positions.length === 0 ? <p className="text-xs text-zinc-500">Zatím žádné pozice. Bez pozice chodí „spontánní přihlášky".</p> : (
            <div className="space-y-2" data-testid="positions-list">
              {positions.map((p) => (
                <div key={p.id} className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2" data-testid={`position-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${p.active ? "" : "text-zinc-500 line-through"}`}>{p.title}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{[p.location, p.employment_type].filter(Boolean).join(" · ") || "—"}</p>
                  </div>
                  <button onClick={() => togglePosition(p)} className={`p-1 ${p.active ? "text-emerald-400" : "text-zinc-600"}`} title={p.active ? "Aktivní – skrýt" : "Skryté – zobrazit"} data-testid={`position-toggle-${p.id}`}><Power className="w-4 h-4" /></button>
                  <button onClick={() => delPosition(p)} className="p-1 text-zinc-600 hover:text-red-400" data-testid={`position-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-2">
        <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-3 inline-flex items-center gap-2"><Briefcase className="w-5 h-5 text-orange-400" /> Přihlášky ({apps.length})</h3>
        {apps.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="apps-empty">Zatím žádné přihlášky.</div>
        ) : (
          <div className="space-y-3" data-testid="apps-list">
            {apps.map((a) => (
              <div key={a.id} className="glass rounded-xl p-4" data-testid={`app-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{a.name} <span className="text-xs text-zinc-500">· {a.position_title}</span></p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-400 mt-1">
                      <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-orange-300"><Mail className="w-3 h-3" /> {a.email}</a>
                      {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 hover:text-orange-300"><Phone className="w-3 h-3" /> {a.phone}</a>}
                      <span>{fmtD(a.created_at)}</span>
                    </div>
                    {a.message && <p className="text-sm text-zinc-300 mt-2 whitespace-pre-line">{a.message}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {a.has_cv && <button onClick={() => openPdf(`/admin/careers/applications/${a.id}/cv`, token)} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-300 hover:bg-orange-500/25" data-testid={`app-cv-${a.id}`}><FileText className="w-3.5 h-3.5" /> CV</button>}
                    <button onClick={() => delApp(a)} className="text-zinc-600 hover:text-red-400 p-1" data-testid={`app-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {STATUS.map((s) => (
                    <button key={s.key} onClick={() => setStatus(a, s.key)} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${a.status === s.key ? s.cls + " border-transparent" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`} data-testid={`app-status-${a.id}-${s.key}`}>{s.label}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CareersTab;
